import type React from "react";

import type { JobWithDetails } from "@/api/hooks/useJob";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";
import {
  alpha,
  AppBar,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Slide,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";
import DOMPurify from "dompurify";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "wouter";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const DetailRow = ({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value: string | null;
  multiline?: boolean;
}) => {
  if (!value) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        py: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-child": {
          borderBottom: "none",
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </Typography>
      {multiline ? (
        <Typography
          variant="body1"
          sx={{
            color: "text.primary",
            lineHeight: 1.5,
          }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }}
        />
      ) : (
        <Typography
          variant="body1"
          sx={{
            color: "text.primary",
            fontWeight: 500,
          }}
        >
          {value}
        </Typography>
      )}
    </Box>
  );
};

export const InterventionDetailsModal = ({
  job: {
    id: jobId,
    details: { intervention },
    "customer-photos": customerPhotos,
  },
  open,
}: {
  job: JobWithDetails;
  open: boolean;
}) => {
  const { formatMessage, formatDate, formatTime } = useIntl();
  const [, navigate] = useLocation();

  const customerRef = useRef<HTMLDivElement>(null);

  const searchParams = new URLSearchParams(window.location.search);
  const scrollTo = searchParams.get("scroll");

  const formatDateTime = (dateString: string | null): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return `${formatDate(date)} ${formatTime(date)}`;
  };

  useEffect(() => {
    if (scrollTo === "customer") {
      setTimeout(() => {
        customerRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 0);
    }
  }, [scrollTo]);

  const [previewCustomerPhoto, setPreviewCustomerPhoto] = useState<
    string | null
  >(null);

  if (!intervention) return null;

  const {
    customer,
    "start-event": startEvent,
    "start-time": startTime,
  } = intervention;

  return (
    <Dialog
      open={open}
      onClose={() => navigate(`/jobs/${jobId}`)}
      fullScreen
      slots={{ transition: Transition }}
      slotProps={{
        backdrop: {
          transitionDuration: 500,
        },
      }}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "background.default",
        },
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Typography
            variant="h6"
            sx={{
              color: "text.primary",
              fontWeight: 600,
            }}
          >
            {formatMessage({
              defaultMessage: "Intervention Details",
            })}
          </Typography>
          <IconButton
            onClick={() => navigate(`/jobs/${jobId}`)}
            sx={{
              color: "text.primary",
              backgroundColor: "action.hover",
              "&:hover": {
                backgroundColor: "action.selected",
              },
            }}
            aria-label={formatMessage({
              defaultMessage: "Close",
            })}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent
        sx={{
          p: 2,
          backgroundColor: "grey.50",
          "-webkit-overflow-scrolling": "touch",
        }}
      >
        <Stack spacing={2}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Avatar
                  sx={{
                    backgroundColor: "warning.main",
                    width: 32,
                    height: 32,
                    mr: 2,
                  }}
                >
                  <EventIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6" fontWeight={600}>
                  {formatMessage({
                    defaultMessage: "Event",
                  })}
                </Typography>
              </Box>
              <Stack spacing={0}>
                <DetailRow
                  label={formatMessage({
                    defaultMessage: "Category",
                  })}
                  value={startEvent?.category}
                />
                <DetailRow
                  label={formatMessage({
                    defaultMessage: "Event Code",
                  })}
                  value={startEvent?.["event-code"]}
                />
                <DetailRow
                  label={formatMessage({
                    defaultMessage: "Description",
                  })}
                  value={startEvent?.["localized-description"]}
                />
                <DetailRow
                  label={formatMessage({
                    defaultMessage: "Zone",
                  })}
                  value={startEvent?.["zone-number"]}
                />
                <DetailRow
                  label={formatMessage({
                    defaultMessage: "Partition",
                  })}
                  value={startEvent?.["partition-number"]}
                />
              </Stack>
            </CardContent>
          </Card>

          <Card
            ref={customerRef}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Avatar
                  sx={{
                    backgroundColor: "primary.main",
                    width: 32,
                    height: 32,
                    mr: 2,
                  }}
                >
                  <PersonIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6" fontWeight={600}>
                  {formatMessage({
                    defaultMessage: "Customer",
                  })}
                </Typography>
              </Box>
              <Stack spacing={0}>
                <DetailRow
                  label={formatMessage({
                    defaultMessage: "Name",
                  })}
                  value={customer?.name}
                />
                <DetailRow
                  label={formatMessage({
                    defaultMessage: "Account",
                  })}
                  value={customer?.account}
                />
                <DetailRow
                  label={formatMessage({
                    defaultMessage: "Address",
                  })}
                  value={customer?.address}
                />
                <DetailRow
                  label={formatMessage({
                    defaultMessage: "Password",
                  })}
                  value={customer?.passwd}
                />
                <DetailRow
                  label={formatMessage({
                    defaultMessage: "Remarks",
                  })}
                  value={customer?.remarks}
                  multiline
                />
                {customer?.["custom-fields"]?.map(({ name }) => (
                  <DetailRow
                    key={name}
                    label={name}
                    value={customer.custom?.[name] || "-"}
                  />
                ))}
                {!!customerPhotos?.length && (
                  <DetailRow
                    label={formatMessage({
                      defaultMessage: "Photos",
                    })}
                    value=" "
                  />
                )}
                {customerPhotos?.map(({ id, url, description }) => (
                  <Box
                    key={id}
                    sx={(theme) => ({
                      display: "flex",
                      alignItems: "center",
                      p: 1.5,
                      border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.background.paper, 0.5),
                    })}
                  >
                    <Avatar
                      src={url}
                      variant="rounded"
                      sx={{
                        width: 48,
                        height: 48,
                        mr: 2,
                        cursor: "pointer",
                      }}
                      onClick={() => setPreviewCustomerPhoto(url)}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.primary",
                        fontWeight: 500,
                      }}
                    >
                      {description}
                    </Typography>
                  </Box>
                ))}
                <Dialog
                  open={!!previewCustomerPhoto}
                  onClose={() => setPreviewCustomerPhoto(null)}
                  maxWidth="md"
                  fullWidth
                  slotProps={{ paper: { sx: { borderRadius: 2 } } }}
                >
                  {previewCustomerPhoto && (
                    <>
                      <DialogContent sx={{ p: 0 }}>
                        <img
                          src={previewCustomerPhoto}
                          alt="img"
                          style={{
                            width: "100%",
                            height: "auto",
                            maxHeight: "70vh",
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      </DialogContent>
                      <DialogActions
                        sx={{ p: 2, justifyContent: "space-between" }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          width={"100%"}
                          justifyContent="space-between"
                        >
                          <Button onClick={() => setPreviewCustomerPhoto(null)}>
                            {formatMessage({ defaultMessage: "Close" })}
                          </Button>
                        </Stack>
                      </DialogActions>
                    </>
                  )}
                </Dialog>
              </Stack>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Avatar
                  sx={{
                    backgroundColor: "success.main",
                    width: 32,
                    height: 32,
                    mr: 2,
                  }}
                >
                  <AccessTimeIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6" fontWeight={600}>
                  {formatMessage({
                    defaultMessage: "Timing",
                  })}
                </Typography>
              </Box>
              <Stack spacing={0}>
                <DetailRow
                  label={formatMessage({
                    defaultMessage: "Start Time",
                  })}
                  value={formatDateTime(startTime)}
                />
                <DetailRow
                  label={formatMessage({
                    defaultMessage: "Event Sent",
                  })}
                  value={formatDateTime(startEvent?.sent)}
                />
                <DetailRow
                  label={formatMessage({
                    defaultMessage: "Event Arrived",
                  })}
                  value={formatDateTime(startEvent?.arrived)}
                />
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
